import { cn } from "@/lib/utils";

/**
 * Tiny SVG donut that shows how full the model's context window is.
 *
 * Driven by the most recent assistant message's persisted usage
 * (`metadata.usage.input_tokens`). Updates live via the SSE `usage`
 * event the chat-surface forwards, so users get a "time to start a
 * new chat" signal before they degrade the agent's responses.
 *
 * Note: this is a *trailing* indicator — it reports where the previous
 * turn ended, not what the next turn will cost. Treat the thresholds
 * as headroom warnings, not hard limits.
 */
export function ContextDonut({
  tokens,
  max,
  className,
}: {
  tokens: number;
  max: number;
  className?: string;
}) {
  if (!Number.isFinite(tokens) || !Number.isFinite(max) || max <= 0) return null;
  const pct = Math.min(1, Math.max(0, tokens / max));
  // Amber at 60%, red at 80% — gives roughly a turn or two of headroom
  // for the user to wrap up before the model degrades.
  const color =
    pct >= 0.8 ? "text-red-500" : pct >= 0.6 ? "text-amber-500" : "text-emerald-500";

  // SVG donut math: r=8 → circumference ≈ 50.27. We rotate -90° so 0%
  // starts at the top and fill grows clockwise.
  const r = 8;
  const c = 2 * Math.PI * r;
  const dash = c * pct;

  const tokensFmt = formatTokens(tokens);
  const maxFmt = formatTokens(max);
  const pctFmt = `${Math.round(pct * 100)}%`;

  return (
    <div
      className={cn("inline-flex items-center gap-1.5 shrink-0", className)}
      title={`${tokensFmt} / ${maxFmt} input tokens · ${pctFmt} of context · last turn`}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" className={color} aria-hidden="true">
        <circle
          cx="10"
          cy="10"
          r={r}
          fill="none"
          strokeWidth="3"
          className="stroke-current opacity-20"
        />
        <circle
          cx="10"
          cy="10"
          r={r}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          className="stroke-current transition-[stroke-dasharray] duration-500"
          strokeDasharray={`${dash} ${c - dash}`}
          transform="rotate(-90 10 10)"
        />
      </svg>
      <span className={cn("text-[11px] tabular-nums", color)}>{pctFmt}</span>
    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/**
 * Resolve a context window for a given model id, with a sensible
 * fallback when the agent's adapterConfig.model isn't recognised.
 * Mirrors the values in `model_catalogue` so the UI doesn't need a
 * server roundtrip to render the donut.
 */
export function resolveContextWindow(modelId: string | null | undefined): number {
  if (!modelId) return 200_000;
  const m = modelId.toLowerCase();
  if (m.includes("opus-4-7")) return 1_000_000;
  if (m.includes("opus")) return 200_000;
  if (m.includes("sonnet")) return 200_000;
  if (m.includes("haiku")) return 200_000;
  return 200_000;
}
